/**
 * GoogleSignInButton — renders Google's own "Sign in with Google" button via
 * Google Identity Services (GIS) and hands the resulting ID token credential
 * up to the caller. The caller is responsible for POSTing that credential to
 * the backend (/auth/google or /vendor/auth/google) and handling the result.
 *
 * Renders nothing if REACT_APP_GOOGLE_CLIENT_ID isn't configured, so pages
 * using this component degrade gracefully before Google OAuth is set up.
 */
import React, { useEffect, useRef, useState } from 'react';

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
export const GOOGLE_SIGNIN_ENABLED = Boolean(CLIENT_ID);

let gsiScriptPromise = null;
function loadGsiScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gsiScriptPromise) return gsiScriptPromise;
  gsiScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services')));
      if (window.google?.accounts?.id) resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
  return gsiScriptPromise;
}

// Google's own compact circular icon button (type: 'icon', shape: 'circle')
// — the "log in with a round provider icon" look most sites use — sized to
// match the custom 40px circles used for the Microsoft/Zoho buttons next to it.
const GoogleSignInButton = ({ onCredential, disabled = false }) => {
  const containerRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;
    loadGsiScript()
      .then(() => { if (!cancelled) setReady(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready || !containerRef.current || !window.google?.accounts?.id) return;
    window.google.accounts.id.initialize({
      client_id: CLIENT_ID,
      callback: (response) => onCredentialRef.current?.(response.credential),
    });
    containerRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(containerRef.current, {
      type: 'icon',
      shape: 'circle',
      theme: 'outline',
      size: 'large',
    });
  }, [ready]);

  if (!CLIENT_ID) return null;

  return (
    <div
      ref={containerRef}
      data-testid="google-signin-button"
      style={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        display: 'flex',
        justifyContent: 'center',
      }}
    />
  );
};

export default GoogleSignInButton;
